// src/components/blog/ArticleBody.tsx
//
// Client-side component that renders markdown content with react-markdown,
// GitHub-flavored markdown support, and auto-generated slugged ids on h2/h3
// for table-of-contents linking.

'use client'

import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
}

// Extract plain text from react-markdown's children prop so the slug is
// generated from the heading text rather than from the React element tree.
function childrenToText(children: React.ReactNode): string {
  if (typeof children === 'string') return children
  if (typeof children === 'number') return String(children)
  if (Array.isArray(children)) return children.map(childrenToText).join('')
  if (children && typeof children === 'object' && 'props' in (children as object)) {
    // @ts-expect-error narrowed above
    return childrenToText(children.props.children)
  }
  return ''
}

export interface ArticleBodyProps {
  content: string
}

export default function ArticleBody({ content }: ArticleBodyProps) {
  return (
    <div className="blog-article-content prose dark:prose-invert max-w-none prose-headings:scroll-mt-24 prose-a:text-brand-ink dark:prose-a:text-brand-ink prose-a:no-underline hover:prose-a:text-brand-ink dark:hover:prose-a:text-brand-ink prose-code:rounded prose-code:bg-slate-100 dark:prose-code:bg-card prose-code:px-1.5 prose-code:py-0.5 prose-code:text-sm prose-code:font-normal prose-code:text-brand-ink dark:prose-code:text-brand-ink prose-code:before:content-none prose-code:after:content-none prose-pre:bg-background prose-blockquote:border-l-cyan-600 dark:prose-blockquote:border-l-cyan-400 prose-strong:text-foreground prose-table:text-sm">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={{
          h2: ({ children, ...props }) => (
            <h2 id={slugify(childrenToText(children))} {...props}>
              {children}
            </h2>
          ),
          h3: ({ children, ...props }) => (
            <h3 id={slugify(childrenToText(children))} {...props}>
              {children}
            </h3>
          ),
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  )
}
